import { RefObject } from 'react';
import axios from 'axios';
import { useMutation } from 'react-query';
import { API } from '../../../CommonUtils/axiosWithDefaults';

export const MAX_FILE_SIZE = 50; // MB

export interface UploadProgressRef {
  onUploadProgress(p: any): void;
  onUploadSuccess(): void;
  bindOnCancel(controller: AbortController): void;
  onCancel(): void;
  onError(): void;
}

interface MutationParams {
  uploadProgressRef?: RefObject<UploadProgressRef>;
}

interface UploadFileParams {
  clientId: string;
  file: File;
  accountId: string;
  documentType: string;
}

interface UploadDocumentDTO {
  snapId: string;
  fileName: string;
}

export const useUploadDocumentMutation = ({ uploadProgressRef }: MutationParams) => {
  return useMutation(async ({ file, clientId, accountId, documentType }: UploadFileParams) => {
    if (file.size >= MAX_FILE_SIZE * 1e6) {
      throw new Error(`Please upload a file up to ${MAX_FILE_SIZE}MB`);
    }

    const controller = new AbortController();

    try {
      if (uploadProgressRef?.current) {
        uploadProgressRef.current.bindOnCancel(controller);
      }

      const formData = new FormData();

      formData.append('clientId', clientId);
      formData.append('files', file);
      formData.append(
        'json',
        JSON.stringify({
          managedAccountId: accountId,
          document: { fileType: documentType, fileName: file.name, tag: 'DOCUMENT' },
        }),
      );

      const response = await API.post('/Consumer/ManagedAccounts/UploadDocument', formData, {
        signal: controller.signal,
        onUploadProgress: p => {
          if (uploadProgressRef?.current) {
            uploadProgressRef.current.onUploadProgress(p);
          }
        },
      });

      if (uploadProgressRef?.current) {
        uploadProgressRef.current.onUploadSuccess();
      }

      return (response as unknown) as UploadDocumentDTO;
    } catch (err) {
      if (uploadProgressRef?.current) {
        if (axios.isCancel(err)) {
          uploadProgressRef.current.onCancel();
        } else {
          uploadProgressRef.current.onError();
        }
      }
      throw err;
    }
  });
};
